/**
 * FinanceDashboard.jsx — Iter 60x (Aug 2026)
 *
 * The Business Intelligence surface for revenue, subscription health, plan
 * breakdown, upcoming renewals, and forward-looking revenue projections.
 *
 * All data comes from `/api/admin/finance/*` endpoints (admin-only, 403 for
 * anyone else). Lazy-loaded from AdminDashboard.js so the main admin bundle
 * stays lean.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, API_BASE, authedFetch } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, Users, CreditCard, Repeat,
  CalendarClock, AlertCircle, PauseCircle, XCircle, CheckCircle2,
  Sparkles, Wallet, PiggyBank, BarChart3, Download, Search,
  ArrowUpRight, ArrowDownRight, Clock, Loader2, FileText,
} from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const INR2 = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PCT = (n) => `${Number(n || 0).toFixed(1)}%`;

// Tailwind's JIT compiler needs literal class names in source. Dynamic
// interpolation like `bg-${color}-50` is invisible to the scanner and
// renders unstyled. So we spell every color combination out here.
const TONE = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600', grad: 'from-emerald-50 to-emerald-100' },
  slate:   { bg: 'bg-slate-100',  text: 'text-slate-700',   icon: 'text-slate-600',   grad: 'from-slate-50 to-slate-100' },
  cyan:    { bg: 'bg-cyan-50',    text: 'text-cyan-700',    icon: 'text-cyan-600',    grad: 'from-cyan-50 to-cyan-100' },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    icon: 'text-blue-600',    grad: 'from-blue-50 to-blue-100' },
  violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  icon: 'text-violet-600',  grad: 'from-violet-50 to-violet-100' },
  rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    icon: 'text-rose-600',    grad: 'from-rose-50 to-rose-100' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'text-amber-600',   grad: 'from-amber-50 to-amber-100' },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-700',  icon: 'text-purple-600',  grad: 'from-purple-50 to-purple-100' },
};

/** One-tap PDF of any finance report — mailed straight to accounts. */
async function downloadFinancePdf(report, params = {}) {
  const qs = new URLSearchParams({ report, ...params }).toString();
  const res = await authedFetch(`${API_BASE}/admin/finance/report.pdf?${qs}`);
  if (!res.ok) throw new Error(await res.text());
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `gurukulveda_${report}_report_${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export function PdfButton({ report, params, label = 'PDF', testid }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await downloadFinancePdf(report, params);
      toast.success('PDF downloaded — ready to mail to accounts');
    } catch {
      toast.error('Could not build that PDF. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={go} disabled={busy} data-testid={testid || `finance-pdf-${report}`}
      title="Download as PDF"
      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-[11px] font-heading transition-colors">
      <FileText className="w-3 h-3" /> {busy ? '…' : label}
    </button>
  );
}

export default function FinanceDashboard() {
  const [overview, setOverview] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [expected, setExpected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    // Fire in parallel — the 4 lightweight aggregates load in one tick.
    Promise.all([
      apiGet('/admin/finance/overview').catch(() => null),
      apiGet('/admin/finance/subscription-analytics').catch(() => null),
      apiGet('/admin/finance/plan-breakdown').catch(() => null),
      apiGet('/admin/finance/expected-revenue').catch(() => null),
    ]).then(([ov, an, br, ex]) => {
      if (!alive) return;
      setOverview(ov); setAnalytics(an); setBreakdown(br); setExpected(ex);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="finance-loading">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-800">Finance</h2>
          <p className="text-xs text-slate-500 font-body">
            Every report below can be downloaded as a PDF and mailed to accounts.
          </p>
        </div>
        <PdfButton report="all" label="Full report (PDF)" testid="finance-pdf-all" />
      </div>
      <RevenueKpis data={overview} />
      <RevenueChart />
      <SubscriptionAnalytics data={analytics} />
      <PlanBreakdown data={breakdown} />
      <UpcomingRenewals />
      <ExpectedRevenue data={expected} />
      <CancellationAnalytics />
      <RevenueLedger />
    </div>
  );
}

/* ==================== Revenue KPIs (11 cards) ==================== */

function RevenueKpis({ data }) {
  if (!data) return null;
  const cards = [
    { label: 'Total Revenue', value: INR(data.total_revenue), icon: DollarSign, color: 'emerald', tid: 'kpi-total-revenue' },
    { label: "Today's Revenue", value: INR(data.today_revenue), icon: TrendingUp, color: 'emerald', tid: 'kpi-today-revenue' },
    { label: 'Yesterday Revenue', value: INR(data.yesterday_revenue), icon: Clock, color: 'slate', tid: 'kpi-yesterday-revenue' },
    { label: 'This Week', value: INR(data.week_revenue), icon: CalendarClock, color: 'cyan', tid: 'kpi-week-revenue' },
    { label: 'This Month', value: INR(data.month_revenue), icon: BarChart3, color: 'blue', tid: 'kpi-month-revenue' },
    { label: 'Annual Revenue', value: INR(data.year_revenue), icon: PiggyBank, color: 'violet', tid: 'kpi-year-revenue' },
    { label: 'ARPU', value: INR2(data.arpu), icon: Users, color: 'rose', tid: 'kpi-arpu', hint: 'Average Revenue Per User' },
    { label: 'Avg Subscription Value', value: INR2(data.avg_subscription_value), icon: CreditCard, color: 'amber', tid: 'kpi-asv' },
    { label: 'MRR', value: INR(data.mrr), icon: Repeat, color: 'emerald', tid: 'kpi-mrr', hint: 'Monthly Recurring Revenue (excludes lifetime plans)' },
    { label: 'ARR', value: INR(data.arr), icon: TrendingUp, color: 'emerald', tid: 'kpi-arr', hint: 'Annual Recurring Revenue = MRR × 12' },
    { label: 'LTV', value: INR2(data.ltv), icon: Wallet, color: 'purple', tid: 'kpi-ltv', hint: 'Lifetime Value = Total revenue ÷ paying users' },
  ];
  return (
    <Card className="p-5 rounded-2xl border-slate-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-base font-semibold text-slate-800">Revenue KPIs</h3>
        <p className="text-[11px] font-body text-slate-500">{data.paying_users} paying · {data.total_paid_orders} orders</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {cards.map(c => {
          const t = TONE[c.color] || TONE.slate;
          return (
            <div key={c.label} title={c.hint || c.label}
              data-testid={c.tid}
              className={`p-4 rounded-xl ${t.bg} hover:scale-[1.03] hover:shadow-md transition-all cursor-default`}>
              <c.icon className={`w-4 h-4 ${t.icon} mb-2`} />
              <p className={`font-heading text-lg font-bold ${t.text}`}>{c.value}</p>
              <p className="text-[10px] font-body text-slate-600 mt-1">{c.label}</p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ==================== Revenue Chart (bucket switcher) ==================== */

function RevenueChart() {
  const [bucket, setBucket] = useState('day');
  const [days, setDays] = useState(30);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiGet(`/admin/finance/revenue-timeseries?bucket=${bucket}&days=${days}`)
      .then(r => setSeries(r?.series || []))
      .catch(() => setSeries([]))
      .finally(() => setLoading(false));
  }, [bucket, days]);

  const maxRev = Math.max(...series.map(s => s.revenue), 1);
  const totalPeriod = series.reduce((a, b) => a + (b.revenue || 0), 0);
  const [hover, setHover] = useState(null);

  const w = 800, h = 240, padL = 60, padR = 12, padT = 12, padB = 40;
  const chartW = w - padL - padR, chartH = h - padT - padB;
  const gap = series.length > 0 ? chartW / series.length : 0;
  const barW = Math.max(gap * 0.55, 2);
  const labelEvery = series.length <= 14 ? 1 : series.length <= 30 ? 3 : series.length <= 60 ? 7 : Math.ceil(series.length / 12);

  return (
    <Card className="p-5 rounded-2xl border-slate-100" data-testid="revenue-chart-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">Revenue Trend</h3>
          <p className="text-xs font-body text-slate-500">Total in view: <span className="font-heading text-emerald-600">{INR(totalPeriod)}</span></p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            {[
              { k: 'day', l: 'Daily' },
              { k: 'week', l: 'Weekly' },
              { k: 'month', l: 'Monthly' },
              { k: 'year', l: 'Yearly' },
            ].map(b => (
              <button key={b.k} onClick={() => setBucket(b.k)}
                data-testid={`revenue-bucket-${b.k}`}
                className={`px-2.5 py-1 text-[11px] font-heading rounded-lg transition-colors ${
                  bucket === b.k ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>{b.l}</button>
            ))}
          </div>
          <span className="w-px h-4 bg-slate-200" />
          <div className="flex items-center gap-1">
            {[
              { k: 7, l: '7d' },
              { k: 30, l: '30d' },
              { k: 90, l: '90d' },
              { k: 365, l: '1y' },
            ].map(d => (
              <button key={d.k} onClick={() => setDays(d.k)}
                data-testid={`revenue-days-${d.k}`}
                className={`px-2.5 py-1 text-[11px] font-heading rounded-lg transition-colors ${
                  days === d.k ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>{d.l}</button>
            ))}
          </div>
        </div>
      </div>
      {loading ? (
        <div className="h-56 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : series.length === 0 ? (
        <p className="text-sm text-slate-400 font-body text-center py-8">No revenue in this window.</p>
      ) : (
        <div className="relative">
          <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="revenue-chart-svg">
            {[0, 0.25, 0.5, 0.75, 1].map(f => {
              const y = padT + chartH - f * chartH;
              const val = Math.round(maxRev * f);
              return (
                <g key={f}>
                  <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="4,4" />
                  <text x={padL - 6} y={y + 4} textAnchor="end" fill="#94A3B8" fontSize="10" fontFamily="DM Sans">
                    ₹{val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}
                  </text>
                </g>
              );
            })}
            {series.map((d, i) => {
              const barH = Math.max((d.revenue / maxRev) * chartH, d.revenue > 0 ? 3 : 0);
              const x = padL + i * gap + (gap - barW) / 2;
              const y = padT + chartH - barH;
              const active = hover === i;
              const showLabel = i % labelEvery === 0 || i === series.length - 1;
              return (
                <g key={i}>
                  <rect x={padL + i * gap} y={padT} width={gap} height={chartH} fill="transparent"
                    onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                    data-testid={`revenue-hitbox-${i}`} />
                  <rect x={x} y={y} width={barW} height={barH} rx="3"
                    fill={active ? '#059669' : '#10B981'} opacity={active ? 1 : 0.85} />
                  {showLabel && (
                    <text x={x + barW / 2} y={h - 8} textAnchor="middle" fill="#64748B" fontSize="9" fontFamily="DM Sans">
                      {d.date.length > 7 ? d.date.slice(5) : d.date}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          {hover !== null && series[hover] && (
            <div className="absolute top-2 right-3 bg-slate-800 text-white rounded-xl px-3 py-2 shadow-xl text-xs font-body pointer-events-none z-10" data-testid="revenue-tooltip">
              <p className="font-heading text-[11px] text-emerald-300 mb-0.5">{series[hover].date}</p>
              <p><span className="text-slate-400">Revenue:</span> <span className="font-heading">{INR(series[hover].revenue)}</span></p>
              <p><span className="text-slate-400">Orders:</span> <span className="font-heading">{series[hover].order_count}</span></p>
              {series[hover].growth_pct !== null && (
                <p><span className="text-slate-400">Growth:</span>{' '}
                  <span className={`font-heading inline-flex items-center gap-0.5 ${series[hover].growth_pct >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {series[hover].growth_pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {Math.abs(series[hover].growth_pct)}%
                  </span>
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* ==================== Subscription Analytics ==================== */

function SubscriptionAnalytics({ data }) {
  if (!data) return null;
  const cards = [
    { label: 'Active', value: data.active, icon: CheckCircle2, color: 'emerald', tid: 'sub-active' },
    { label: 'Expired', value: data.expired, icon: AlertCircle, color: 'amber', tid: 'sub-expired' },
    { label: 'Cancelled', value: data.cancelled, icon: XCircle, color: 'rose', tid: 'sub-cancelled' },
    { label: 'Paused', value: data.paused, icon: PauseCircle, color: 'slate', tid: 'sub-paused' },
    { label: 'Trial Users', value: data.trial, icon: Sparkles, color: 'purple', tid: 'sub-trial' },
    { label: 'Converted', value: data.converted, icon: TrendingUp, color: 'cyan', tid: 'sub-converted' },
    { label: 'Conversion %', value: PCT(data.conversion_pct), icon: TrendingUp, color: 'emerald', tid: 'sub-conversion-pct' },
    { label: 'Renewal %', value: PCT(data.renewal_pct), icon: Repeat, color: 'blue', tid: 'sub-renewal-pct' },
  ];
  return (
    <Card className="p-5 rounded-2xl border-slate-100">
      <h3 className="font-heading text-base font-semibold text-slate-800 mb-4">Subscription Analytics</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map(c => {
          const t = TONE[c.color] || TONE.slate;
          return (
            <div key={c.label} data-testid={c.tid}
              className={`p-4 rounded-xl ${t.bg} flex items-center gap-3 hover:scale-[1.02] transition-transform`}>
              <c.icon className={`w-5 h-5 ${t.icon}`} />
              <div>
                <p className={`font-heading text-xl font-bold ${t.text}`}>{c.value}</p>
                <p className="text-[10px] font-body text-slate-600">{c.label}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ==================== Plan Breakdown ==================== */

function PlanBreakdown({ data }) {
  if (!data?.buckets) return null;
  return (
    <Card className="p-5 rounded-2xl border-slate-100" data-testid="plan-breakdown-card">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-heading text-base font-semibold text-slate-800">Plan Breakdown</h3>
        <PdfButton report="plans" testid="plans-export-pdf" />
      </div>
      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full text-sm font-body min-w-[560px]">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500 text-[11px] font-heading uppercase">
              <th className="py-2 text-left">Plan</th>
              <th className="py-2 text-right">Users</th>
              <th className="py-2 text-right">Orders</th>
              <th className="py-2 text-right">Revenue</th>
              <th className="py-2 text-right">Renewal %</th>
              <th className="py-2 text-right">Cancellation %</th>
            </tr>
          </thead>
          <tbody>
            {data.buckets.map(b => (
              <tr key={b.bucket} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`plan-row-${b.bucket}`}>
                <td className="py-3 font-heading text-slate-700">{b.label}</td>
                <td className="py-3 text-right">{b.users}</td>
                <td className="py-3 text-right text-slate-500">{b.orders}</td>
                <td className="py-3 text-right font-heading text-emerald-600">{INR(b.revenue)}</td>
                <td className="py-3 text-right">{PCT(b.renewal_pct)}</td>
                <td className="py-3 text-right text-rose-500">{PCT(b.cancellation_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ==================== Upcoming Renewals table ==================== */

function UpcomingRenewals() {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('days_remaining');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const limit = 20;
  const [data, setData] = useState({ total: 0, rows: [] });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ q, sort, order, page: String(page), limit: String(limit) }).toString();
    apiGet(`/admin/finance/upcoming-renewals?${qs}`)
      .then(setData).catch(() => setData({ total: 0, rows: [] }))
      .finally(() => setLoading(false));
  }, [q, sort, order, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSort = (col) => {
    if (sort === col) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setSort(col); setOrder('asc'); }
  };

  const exportCsv = useCallback(async () => {
    try {
      const res = await authedFetch(`${API_BASE}/admin/finance/upcoming-renewals.csv?q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gurukulveda_upcoming_renewals_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error('Could not download CSV');
    }
  }, [q]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / limit));

  return (
    <Card className="p-5 rounded-2xl border-slate-100" data-testid="upcoming-renewals-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">Upcoming Renewals</h3>
          <p className="text-xs font-body text-slate-500">{data.total} renewals in the next 90 days</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={e => { setQ(e.target.value); setPage(1); }}
              placeholder="Search name, email, phone…"
              data-testid="renewals-search"
              className="pl-7 pr-3 py-1.5 text-xs font-body rounded-lg border border-slate-200 focus:border-emerald-400 focus:outline-none w-56"
            />
          </div>
          <button onClick={exportCsv} data-testid="renewals-export-csv"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-[11px] font-heading transition-colors">
            <Download className="w-3 h-3" /> CSV
          </button>
          <PdfButton report="renewals" params={{ q }} testid="renewals-export-pdf" />
        </div>
      </div>
      {loading ? (
        <div className="py-8 flex items-center justify-center"><Loader2 className="w-5 h-5 text-emerald-500 animate-spin" /></div>
      ) : data.rows.length === 0 ? (
        <p className="text-sm text-slate-400 font-body text-center py-8">No upcoming renewals in this window.</p>
      ) : (
        <>
          <div className="overflow-x-auto -mx-2 px-2">
            <table className="w-full text-sm font-body min-w-[900px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 text-[11px] font-heading uppercase">
                  {[
                    { k: 'user_name', l: 'User' },
                    { k: 'email', l: 'Email' },
                    { k: 'phone', l: 'Phone' },
                    { k: 'subscription', l: 'Plan' },
                    { k: 'renewal_date', l: 'Renewal Date' },
                    { k: 'renewal_amount', l: 'Amount' },
                    { k: 'days_remaining', l: 'Days Left' },
                    { k: 'status', l: 'Status' },
                  ].map(col => (
                    <th key={col.k} onClick={() => toggleSort(col.k)}
                      data-testid={`renewals-sort-${col.k}`}
                      className={`py-2 text-left cursor-pointer hover:text-emerald-600 select-none ${['renewal_amount', 'days_remaining'].includes(col.k) ? 'text-right' : ''}`}>
                      {col.l} {sort === col.k && (order === 'asc' ? '▲' : '▼')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`renewal-row-${r.user_id}`}>
                    <td className="py-2.5 font-heading text-slate-700">{r.user_name}</td>
                    <td className="py-2.5 text-slate-600">{r.email}</td>
                    <td className="py-2.5 text-slate-500">{r.phone}</td>
                    <td className="py-2.5 text-slate-600">{r.subscription}</td>
                    <td className="py-2.5 text-slate-600">{r.renewal_date}</td>
                    <td className="py-2.5 text-right font-heading text-emerald-600">{INR(r.renewal_amount)}</td>
                    <td className="py-2.5 text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-heading ${
                        r.days_remaining <= 7 ? 'bg-rose-50 text-rose-700' :
                        r.days_remaining <= 30 ? 'bg-amber-50 text-amber-700' :
                        'bg-slate-50 text-slate-600'
                      }`}>{r.days_remaining}d</span>
                    </td>
                    <td className="py-2.5"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-heading ${
                      r.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-xs font-body text-slate-500">
              <span>Page {page} of {totalPages}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  data-testid="renewals-prev"
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40">Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  data-testid="renewals-next"
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/* ==================== Expected Revenue predictions ==================== */

function ExpectedRevenue({ data }) {
  if (!data) return null;
  const cards = [
    { label: 'Next 7 Days', key: 'next_7_days', color: 'emerald', tid: 'exp-7' },
    { label: 'Next 30 Days', key: 'next_30_days', color: 'cyan', tid: 'exp-30' },
    { label: 'Next Quarter', key: 'next_quarter', color: 'blue', tid: 'exp-quarter' },
    { label: 'Next Annual', key: 'next_annual', color: 'violet', tid: 'exp-annual' },
  ];
  return (
    <Card className="p-5 rounded-2xl border-slate-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-base font-semibold text-slate-800">Expected Revenue (if every current subscriber renews)</h3>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => {
          const d = data[c.key] || { revenue: 0, renewals: 0 };
          const t = TONE[c.color] || TONE.slate;
          return (
            <div key={c.key} data-testid={c.tid}
              className={`p-4 rounded-xl bg-gradient-to-br ${t.grad} hover:scale-[1.02] transition-transform`}>
              <p className="text-[10px] font-heading uppercase text-slate-500 mb-1">{c.label}</p>
              <p className={`font-heading text-2xl font-bold ${t.text}`}>{INR(d.revenue)}</p>
              <p className="text-[11px] font-body text-slate-500 mt-1">{d.renewals} renewals</p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}


/* ==================== Cancellation Analytics ==================== */

function CancellationAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet('/admin/finance/cancellation-analytics?trend_days=30')
      .then(setData).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="p-5 rounded-2xl border-slate-100 flex items-center justify-center py-10" data-testid="cancellation-analytics-loading">
        <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
      </Card>
    );
  }
  if (!data) return null;

  const REASON_COLORS = {
    too_expensive: '#F43F5E',
    not_useful: '#F59E0B',
    technical_issues: '#EF4444',
    found_alternative: '#8B5CF6',
    temporary_break: '#06B6D4',
    other: '#64748B',
  };

  const totalReason = data.reasons.reduce((a, r) => a + r.count, 0) || 1;
  const maxTrend = Math.max(...data.trend_daily.map(d => d.count), 1);

  const counts = [
    { label: 'Today', value: data.counts.today, color: 'rose', tid: 'cancel-count-today' },
    { label: 'This Week', value: data.counts.week, color: 'amber', tid: 'cancel-count-week' },
    { label: 'This Month', value: data.counts.month, color: 'blue', tid: 'cancel-count-month' },
    { label: 'This Year', value: data.counts.year, color: 'violet', tid: 'cancel-count-year' },
  ];

  // Pie chart geometry
  const cx = 100, cy = 100, r = 70;
  let cursorAngle = -Math.PI / 2;
  const slices = data.reasons.filter(x => x.count > 0).map(rr => {
    const pct = rr.count / totalReason;
    const start = cursorAngle;
    const end = cursorAngle + pct * 2 * Math.PI;
    cursorAngle = end;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const large = pct > 0.5 ? 1 : 0;
    return {
      d: `M ${cx},${cy} L ${x1},${y1} A ${r},${r} 0 ${large} 1 ${x2},${y2} Z`,
      color: REASON_COLORS[rr.reason] || '#64748B',
      label: rr.label,
      count: rr.count,
      pct,
    };
  });

  return (
    <Card className="p-5 rounded-2xl border-slate-100" data-testid="cancellation-analytics">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">Cancellation Analytics</h3>
          <p className="text-xs font-body text-slate-500">Total cancellations logged: {data.total}</p>
        </div>
      </div>

      {/* Count cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {counts.map(c => {
          const t = TONE[c.color] || TONE.slate;
          return (
            <div key={c.label} data-testid={c.tid} className={`p-4 rounded-xl ${t.bg} hover:scale-[1.02] transition-transform`}>
              <p className="text-[10px] font-heading uppercase text-slate-500">Cancelled {c.label}</p>
              <p className={`font-heading text-2xl font-bold ${t.text} mt-1`}>{c.value}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Bar chart of reasons */}
        <div>
          <h4 className="font-heading text-sm font-semibold text-slate-700 mb-3">Reasons (Bar)</h4>
          <div className="space-y-2" data-testid="cancel-reasons-bar">
            {data.reasons.map(rr => {
              const pct = rr.count === 0 ? 0 : Math.round((rr.count / (data.reasons[0].count || 1)) * 100);
              return (
                <div key={rr.reason} className="flex items-center gap-2">
                  <p className="text-xs font-body text-slate-600 w-32 truncate">{rr.label}</p>
                  <div className="flex-1 relative h-6 bg-slate-100 rounded-lg overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: REASON_COLORS[rr.reason] || '#64748B' }}
                    />
                    <span className="absolute inset-0 flex items-center justify-end pr-2 text-[11px] font-heading text-slate-700">
                      {rr.count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pie chart */}
        <div>
          <h4 className="font-heading text-sm font-semibold text-slate-700 mb-3">Distribution (Pie)</h4>
          <div className="flex items-center gap-4" data-testid="cancel-reasons-pie">
            <svg viewBox="0 0 200 200" className="w-40 h-40 flex-shrink-0">
              {slices.length === 0 ? (
                <circle cx="100" cy="100" r="70" fill="#F1F5F9" />
              ) : (
                slices.map((s, i) => <path key={i} d={s.d} fill={s.color} stroke="white" strokeWidth="2" />)
              )}
            </svg>
            <div className="space-y-1 flex-1">
              {slices.length === 0 ? (
                <p className="text-xs font-body text-slate-400">No cancellations yet.</p>
              ) : slices.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-xs font-body text-slate-600">
                  <span className="w-3 h-3 rounded-sm" style={{ background: s.color }} />
                  <span className="flex-1">{s.label}</span>
                  <span className="font-heading text-slate-800">{(s.pct * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Trend line */}
      <div className="mt-5">
        <h4 className="font-heading text-sm font-semibold text-slate-700 mb-2">Trend (last 30 days)</h4>
        <svg viewBox="0 0 800 140" className="w-full" data-testid="cancel-trend-svg">
          {[0, 0.5, 1].map(f => {
            const y = 20 + (1 - f) * 100;
            return <line key={f} x1="40" y1={y} x2="790" y2={y} stroke="#E2E8F0" strokeWidth="0.5" strokeDasharray="4,4" />;
          })}
          {(() => {
            const stepX = (790 - 40) / Math.max(data.trend_daily.length - 1, 1);
            const pts = data.trend_daily.map((d, i) => {
              const x = 40 + i * stepX;
              const y = 120 - (d.count / maxTrend) * 100;
              return `${x},${y}`;
            });
            return (
              <>
                <polyline points={pts.join(' ')} fill="none" stroke="#F43F5E" strokeWidth="2" />
                {data.trend_daily.map((d, i) => {
                  const x = 40 + i * stepX;
                  const y = 120 - (d.count / maxTrend) * 100;
                  return <circle key={i} cx={x} cy={y} r="2.5" fill="#F43F5E">
                    <title>{d.date}: {d.count} cancellation{d.count === 1 ? '' : 's'}</title>
                  </circle>;
                })}
              </>
            );
          })()}
          {data.trend_daily.map((d, i) => {
            if (i % Math.ceil(data.trend_daily.length / 8) !== 0 && i !== data.trend_daily.length - 1) return null;
            const stepX = (790 - 40) / Math.max(data.trend_daily.length - 1, 1);
            const x = 40 + i * stepX;
            return <text key={i} x={x} y={135} textAnchor="middle" fontSize="9" fill="#94A3B8" fontFamily="DM Sans">{d.date.slice(5)}</text>;
          })}
        </svg>
      </div>
    </Card>
  );
}

/* ==================== Revenue Ledger table ==================== */

function RevenueLedger() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const limit = 25;
  const [data, setData] = useState({ total: 0, rows: [], sum_amount: 0, sum_gst: 0 });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams({ q, status, page: String(page), limit: String(limit) }).toString();
    apiGet(`/admin/finance/revenue-ledger?${qs}`)
      .then(setData).catch(() => setData({ total: 0, rows: [], sum_amount: 0, sum_gst: 0 }))
      .finally(() => setLoading(false));
  }, [q, status, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCsv = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ q, status }).toString();
      const res = await authedFetch(`${API_BASE}/admin/finance/revenue-ledger.csv?${qs}`);
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gurukulveda_revenue_ledger_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Could not download CSV');
    }
  }, [q, status]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / limit));
  const STATUS_TONE = {
    paid: 'bg-emerald-50 text-emerald-700',
    created: 'bg-amber-50 text-amber-700',
    verification_failed: 'bg-rose-50 text-rose-700',
  };

  return (
    <Card className="p-5 rounded-2xl border-slate-100" data-testid="revenue-ledger-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">Revenue Ledger</h3>
          <p className="text-xs font-body text-slate-500">
            {data.total} transactions · Total: <span className="font-heading text-emerald-600">{INR(data.sum_amount)}</span> · GST collected: <span className="font-heading text-slate-700">{INR(data.sum_gst)}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={e => { setQ(e.target.value); setPage(1); }}
              placeholder="Search invoice, email, plan, txn…"
              data-testid="ledger-search"
              className="pl-7 pr-3 py-1.5 text-xs font-body rounded-lg border border-slate-200 focus:border-emerald-400 focus:outline-none w-56"
            />
          </div>
          <select
            data-testid="ledger-status-filter"
            value={status}
            onChange={e => { setStatus(e.target.value); setPage(1); }}
            className="px-2 py-1.5 text-xs font-body rounded-lg border border-slate-200 focus:border-emerald-400 focus:outline-none">
            <option value="">All statuses</option>
            <option value="paid">Paid</option>
            <option value="created">Awaiting</option>
            <option value="verification_failed">Failed</option>
          </select>
          <button onClick={exportCsv} data-testid="ledger-export-csv"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-[11px] font-heading transition-colors">
            <Download className="w-3 h-3" /> CSV
          </button>
          <PdfButton report="ledger" params={{ q, status }} testid="ledger-export-pdf" />
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center"><Loader2 className="w-5 h-5 text-emerald-500 animate-spin" /></div>
      ) : data.rows.length === 0 ? (
        <p className="text-sm text-slate-400 font-body text-center py-8">No transactions match this filter.</p>
      ) : (
        <>
          <div className="overflow-x-auto -mx-2 px-2">
            <table className="w-full text-xs font-body min-w-[1100px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 text-[10px] font-heading uppercase">
                  <th className="py-2 text-left">Invoice #</th>
                  <th className="py-2 text-left">User</th>
                  <th className="py-2 text-left">Plan</th>
                  <th className="py-2 text-right">Amount</th>
                  <th className="py-2 text-right">Net</th>
                  <th className="py-2 text-right">GST</th>
                  <th className="py-2 text-right">Disc.</th>
                  <th className="py-2 text-left">Gateway</th>
                  <th className="py-2 text-left">Status</th>
                  <th className="py-2 text-left">Txn ID</th>
                  <th className="py-2 text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.invoice_number} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ledger-row-${r.invoice_number}`}>
                    <td className="py-2 font-heading text-slate-700 truncate max-w-[140px]" title={r.invoice_number}>{r.invoice_number}</td>
                    <td className="py-2">
                      <div className="text-slate-700">{r.user_name}</div>
                      <div className="text-[10px] text-slate-400">{r.user_email}</div>
                    </td>
                    <td className="py-2 text-slate-600">{r.plan_label}</td>
                    <td className="py-2 text-right font-heading text-emerald-600">{INR(r.amount)}</td>
                    <td className="py-2 text-right text-slate-500">{INR(r.net_amount)}</td>
                    <td className="py-2 text-right text-slate-500">{INR(r.gst_amount)}</td>
                    <td className="py-2 text-right text-slate-400">{INR(r.discount)}</td>
                    <td className="py-2 text-slate-500 truncate max-w-[100px]" title={r.gateway}>{r.gateway}</td>
                    <td className="py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-heading ${STATUS_TONE[r.payment_status] || 'bg-slate-100 text-slate-600'}`}>{r.payment_status}</span>
                    </td>
                    <td className="py-2 text-slate-500 truncate max-w-[140px]" title={r.transaction_id}>{r.transaction_id}</td>
                    <td className="py-2 text-slate-500">{r.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-xs font-body text-slate-500">
              <span>Page {page} of {totalPages}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  data-testid="ledger-prev"
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40">Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  data-testid="ledger-next"
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

